import React, { useEffect, useState } from "react";

import ArrowForward from "@mui/icons-material/ArrowForward";
import Done from "@mui/icons-material/Done";
import WarningAmber from "@mui/icons-material/WarningAmber";
import { Stack, Typography, useMediaQuery } from "@mui/material";

import { useAsyncCurrency } from "../../../Components/hooks/useCurrency";
import { formatDecimalNumber } from "../../../utils/common";
import { useInsightsContext } from "../context";
import { type Insight, RiskTypes, type SavingsPeriod } from "../types";
import { totalCustomProblems, totalPotentialDailySavings, totalRisks } from "../utils";

export const savingsPeriodToLabel = {
  daily: "day",
  monthly: "month",
  yearly: "year",
};

type ImpactSectionUI = {
  text: string;
  icon: React.ReactNode;
  color: string;
};

type InsightImpactPreviewProps = {
  insight: Insight;
  savingsPeriod: SavingsPeriod;
};

const InsightImpactPreview: React.FC<InsightImpactPreviewProps> = ({ insight, savingsPeriod }) => {
  const { calculateSavingsForSelectedPeriod } = useInsightsContext();

  const { asyncConvertCurrency, customerCurrencySymbol } = useAsyncCurrency();

  const [convertedAndFormatted, setConvertedAndFormatted] = useState<string>();

  const potentialDailySavings = totalPotentialDailySavings(insight);

  useEffect(() => {
    if (!potentialDailySavings) {
      return;
    }
    const potentialDailySavingsForPeriod = calculateSavingsForSelectedPeriod(potentialDailySavings);
    asyncConvertCurrency(potentialDailySavingsForPeriod).then((convertedSavings) => {
      setConvertedAndFormatted(`${customerCurrencySymbol}${formatDecimalNumber(convertedSavings, 0)}`);
    });
  }, [asyncConvertCurrency, calculateSavingsForSelectedPeriod, customerCurrencySymbol, insight, potentialDailySavings]);

  const customProblems = totalCustomProblems(insight);

  const risks = RiskTypes.map((risk) => ({
    label: risk.label,
    value: totalRisks(insight, risk.key),
  }));

  const activeRisk = risks.find((risk) => risk.value);

  const isMobile = useMediaQuery("(max-width:650px)");

  const lowerFirstChar = (str: string): string => str.charAt(0).toLowerCase() + str.slice(1);

  const getImpactText = (): string | null => {
    if (potentialDailySavings) {
      return `${convertedAndFormatted}/${savingsPeriodToLabel[savingsPeriod]} of potential savings`;
    } else if (activeRisk) {
      return `${formatDecimalNumber(activeRisk.value, 0)} ${activeRisk.label.toLowerCase()}`;
    } else if (customProblems) {
      let customProblemsUnits =
        (customProblems === 1
          ? insight.results?.customProblems?.unitLongSingular
          : insight.results?.customProblems?.unitLongPlural) ?? "";
      customProblemsUnits = lowerFirstChar(customProblemsUnits);

      return `${formatDecimalNumber(customProblems, 0)} ${customProblemsUnits}`;
    }
    return null;
  };

  const getImpactContent = (): ImpactSectionUI => {
    if (insight.displayStatus === "permissions needed") {
      return {
        color: "warning.main",
        text: "Permissions needed for impact estimation",
        icon: <WarningAmber style={{ width: 18, height: 18 }} />,
      };
    }

    const impactText = getImpactText();
    if (impactText) {
      return {
        color: "primary.main",
        text: impactText,
        icon: <ArrowForward style={{ width: 18, height: 18 }} />,
      };
    }

    return {
      color: "text.secondary",
      text: "No impact detected",
      icon: <Done style={{ width: 18, height: 18 }} />,
    };
  };

  const impactContent = getImpactContent();

  return (
    <Stack
      direction={isMobile ? "column" : "row"}
      spacing={1.5}
      alignItems={isMobile ? "flex-start" : "center"}
      sx={{ mt: 1 }}
      height={isMobile ? "auto" : "28px"}
    >
      <Stack direction="row" color={impactContent.color} spacing={1}>
        {impactContent.icon}
        <Typography data-cy="impact-preview-text" variant="subtitle2" sx={{ mt: "0!important", fontWeight: 500 }}>
          {impactContent.text}
        </Typography>{" "}
      </Stack>
    </Stack>
  );
};

export default InsightImpactPreview;
